import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Store } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSessionContext } from "@/context/SessionContextProvider";

interface StoreSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onStoreSelected: () => void;
  requiredContext: string;
}

export const StoreSelectionDialog = ({ 
  open, 
  onClose, 
  onStoreSelected,
  requiredContext 
}: StoreSelectionDialogProps) => {
  const { networkId, switchToStoreMode } = useSessionContext();

  const { data: stores, isLoading } = useQuery({
    queryKey: ['stores', networkId],
    queryFn: async () => {
      if (!networkId) return [];
      
      const { data, error } = await supabase
        .from('stores')
        .select('id, name')
        .eq('network_id', networkId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: open && !!networkId,
  });

  const handleStoreSelect = async (storeId: string) => {
    await switchToStoreMode(storeId);
    onStoreSelected();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-5 w-5 text-primary" />
            Seleção de Loja Necessária
          </DialogTitle>
          <DialogDescription>
            {requiredContext === 'STORE_REQUIRED' 
              ? 'Esta página requer que você selecione uma loja específica para continuar.'
              : 'Esta página requer contexto específico.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-4">
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">
              Carregando lojas...
            </div>
          ) : stores && stores.length > 0 ? (
            stores.map((store) => (
              <Button
                key={store.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleStoreSelect(store.id)}
              >
                <Store className="h-4 w-4 mr-2" />
                {store.name}
              </Button>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              Nenhuma loja disponível
            </div>
          )}
        </div>

        <Button variant="ghost" onClick={onClose} className="mt-4">
          Voltar
        </Button>
      </DialogContent>
    </Dialog>
  );
};
